import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import regions from "./chinaRegions.json";
import {
  SINGLE_DATING_MARITAL_OPTIONS,
  canAccessSingleDating,
} from "../constants/maritalStatus";
import { getOppositeGender, getOppositeGenderLabel } from "../constants/gender";
import useSeo from "../hooks/useSeo";
import useUserMaritalStatus from "../hooks/useUserMaritalStatus";
import { SEO_PAGES } from "../constants/seo";
import { assetUrl } from "../utils/api";
import { formatRelativeTime } from "../utils/datetime";
import { formatCityLabel } from "../utils/resolveRegion";
import VerificationBadges from "../components/VerificationBadges";

function formatLastActive(iso) {
  const text = formatRelativeTime(iso, {
    justNow: "刚刚",
    activeSuffix: "活跃",
  });
  // 超过 7 天不再展示相对活跃文案
  if (!text || !/前活跃$|刚刚活跃$/.test(text)) return "";
  return text;
}

const selectClass =
  "w-full border border-pink-100 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-200";

export default function DiscoverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  useSeo(SEO_PAGES.discover);
  const { maritalStatus: userMaritalStatus, isMarried } = useUserMaritalStatus();
  const currentUser_id = Number(localStorage.getItem("user_id"));
  const [hasQuestionnaire, setHasQuestionnaire] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [myGender, setMyGender] = useState("");

  const preferredGender = getOppositeGender(myGender);
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [education, setEducation] = useState("");
  const [job, setJob] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [housingStatus, setHousingStatus] = useState("");
  const [religion, setReligion] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [heightMin, setHeightMin] = useState("");
  const [heightMax, setHeightMax] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [memberOnly, setMemberOnly] = useState(false);
  const [questionnaireOnly, setQuestionnaireOnly] = useState(false);
  const [cityPresetApplied, setCityPresetApplied] = useState(false);

  useEffect(() => {
    if (cityPresetApplied) return;
    const presetProvince = searchParams.get("province") || "";
    const presetCity = searchParams.get("city") || "";
    const presetDistrict = searchParams.get("district") || "";
    if (presetProvince || presetCity || presetDistrict) {
      setProvince(presetProvince);
      setCity(presetCity);
      setDistrict(presetDistrict);
    }
    setCityPresetApplied(true);
  }, [searchParams, cityPresetApplied]);

  const provinces = regions.map((p) => ({ name: p.name, code: p.code }));
  const selectedProvince = regions.find((p) => p.name === province);
  const cities = selectedProvince?.children || [];
  const selectedCity = cities.find((c) => c.name === city);
  const districts = selectedCity?.children || [];

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentUser_id) params.append("current_user_id", currentUser_id);
      if (appliedKeyword) params.append("keyword", appliedKeyword);
      if (province) params.append("province", province);
      if (city) params.append("city", city);
      if (district) params.append("district", district);
      if (maritalStatus) params.append("marital_status", maritalStatus);
      if (education) params.append("education", education);
      if (job.trim()) params.append("job", job.trim());
      if (monthlyIncome) params.append("monthly_income", monthlyIncome);
      if (housingStatus) params.append("housing_status", housingStatus);
      if (religion) params.append("religion", religion);
      if (ageMin) params.append("age_min", ageMin);
      if (ageMax) params.append("age_max", ageMax);
      if (heightMin) params.append("height_min", heightMin);
      if (heightMax) params.append("height_max", heightMax);
      if (verifiedOnly) params.append("verified_only", "true");
      if (memberOnly) params.append("member_only", "true");
      if (questionnaireOnly) params.append("has_questionnaire_only", "true");

      const res = await fetch(`/api/users?${params.toString()}`);
      const data = await res.json();
      if (!Array.isArray(data)) return;

      if (currentUser_id) {
        const meRes = await fetch(`/api/profile/${currentUser_id}`);
        const me = meRes.ok ? await meRes.json() : null;
        if (me?.gender) {
          setMyGender(me.gender);
        }
        setHasQuestionnaire(Boolean(me?.has_questionnaire));
      } else {
        setMyGender("");
        setHasQuestionnaire(false);
      }
      setUsers(data);
    } catch (err) {
      console.error("加载用户列表失败:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    currentUser_id,
    appliedKeyword,
    province,
    city,
    district,
    maritalStatus,
    education,
    job,
    monthlyIncome,
    housingStatus,
    religion,
    ageMin,
    ageMax,
    heightMin,
    heightMax,
    verifiedOnly,
    memberOnly,
    questionnaireOnly,
  ]);

  useEffect(() => {
    if (currentUser_id && (isMarried || !canAccessSingleDating(userMaritalStatus))) {
      navigate("/center", { replace: true });
      return;
    }
    if (!cityPresetApplied) return;
    loadUsers();
  }, [
    currentUser_id,
    isMarried,
    userMaritalStatus,
    navigate,
    loadUsers,
    cityPresetApplied,
  ]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setAppliedKeyword(keyword.trim());
  };

  const resetFilters = () => {
    setKeyword("");
    setAppliedKeyword("");
    setProvince("");
    setCity("");
    setDistrict("");
    setMaritalStatus("");
    setEducation("");
    setJob("");
    setMonthlyIncome("");
    setHousingStatus("");
    setReligion("");
    setAgeMin("");
    setAgeMax("");
    setHeightMin("");
    setHeightMax("");
    setVerifiedOnly(false);
    setMemberOnly(false);
    setQuestionnaireOnly(false);
  };

  const activeFilterCount = [
    appliedKeyword,
    province,
    city,
    district,
    maritalStatus,
    education,
    job.trim(),
    monthlyIncome,
    housingStatus,
    religion,
    ageMin,
    ageMax,
    heightMin,
    heightMax,
    verifiedOnly,
    memberOnly,
    questionnaireOnly,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <Header />

      <div className="flex-1 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-1">寻找对象</h1>
            <p className="text-sm text-gray-500 mb-2">
              {currentUser_id && preferredGender
                ? `已登录 · 为您推荐${getOppositeGenderLabel(myGender)} · 认证会员优先`
                : "未登录 · 女会员优先展示 · 登录后将按您的性别推荐异性"}
            </p>
            <Link
              to="/love-map"
              className="inline-flex text-sm text-pink-600 hover:underline"
            >
              先走 AI 爱情地图，认识自己再找人 →
            </Link>
          </div>

          <form
            onSubmit={handleSearch}
            className="bg-white rounded-2xl shadow p-4 mb-6 space-y-4 mt-4"
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索昵称、职业、自我介绍..."
                className="flex-1 border border-pink-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              />
              <button
                type="submit"
                className="px-5 py-2.5 bg-pink-500 text-white rounded-xl text-sm font-medium hover:bg-pink-600 transition"
              >
                搜索
              </button>
            </div>

            <div>
              <p className="text-xs text-gray-400 mb-2">所在地区</p>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={province}
                  onChange={(e) => {
                    setProvince(e.target.value);
                    setCity("");
                    setDistrict("");
                  }}
                  className={selectClass}
                >
                  <option value="">省份</option>
                  {provinces.map((p) => (
                    <option key={p.code} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>

                <select
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setDistrict("");
                  }}
                  className={selectClass}
                  disabled={!province}
                >
                  <option value="">市</option>
                  {cities.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <select
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  className={selectClass}
                  disabled={!city}
                >
                  <option value="">区/县</option>
                  {districts.map((d) => (
                    <option key={d.code} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-400 mb-2">基本条件</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div
                  className={`${selectClass} bg-gray-50 text-gray-600 ${currentUser_id ? "cursor-not-allowed" : ""}`}
                  title={
                    currentUser_id
                      ? "根据您的性别自动匹配异性"
                      : "未登录：女会员排在前面，男会员排在后面"
                  }
                >
                  {currentUser_id
                    ? preferredGender
                      ? getOppositeGenderLabel(myGender)
                      : "性别：请先完善资料"
                    : "全部（女会员优先）"}
                </div>

                <select
                  value={maritalStatus}
                  onChange={(e) => setMaritalStatus(e.target.value)}
                  className={selectClass}
                >
                  <option value="">婚姻状况</option>
                  {SINGLE_DATING_MARITAL_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  placeholder="最小年龄"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                  className={selectClass}
                />
                <input
                  type="number"
                  placeholder="最大年龄"
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                  className={selectClass}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-sm text-pink-500 hover:text-pink-600"
              >
                {showAdvanced ? "收起更多筛选 ▲" : "更多筛选 ▼"}
                {activeFilterCount > 0 ? `（已选 ${activeFilterCount} 项）` : ""}
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                重置全部
              </button>
            </div>

            {showAdvanced && (
              <div className="space-y-4 pt-3 border-t border-pink-50">
                <div>
                  <p className="text-xs text-gray-400 mb-2">学历与职业</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <select
                      value={education}
                      onChange={(e) => setEducation(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">学历</option>
                      <option value="高中">高中</option>
                      <option value="大专">大专</option>
                      <option value="本科">本科</option>
                      <option value="硕士">硕士</option>
                      <option value="博士">博士</option>
                    </select>

                    <select
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">月收入</option>
                      <option value="5k以下">5k以下</option>
                      <option value="5k-10k">5k-10k</option>
                      <option value="10k-20k">10k-20k</option>
                      <option value="20k-50k">20k-50k</option>
                      <option value="50k以上">50k以上</option>
                    </select>

                    <input
                      type="text"
                      placeholder="职业关键词"
                      value={job}
                      onChange={(e) => setJob(e.target.value)}
                      className={selectClass}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-2">身材</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="最低身高(cm)"
                      value={heightMin}
                      onChange={(e) => setHeightMin(e.target.value)}
                      className={selectClass}
                    />
                    <input
                      type="number"
                      placeholder="最高身高(cm)"
                      value={heightMax}
                      onChange={(e) => setHeightMax(e.target.value)}
                      className={selectClass}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-2">生活背景</p>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={housingStatus}
                      onChange={(e) => setHousingStatus(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">住房情况</option>
                      <option value="已购房">已购房</option>
                      <option value="租房">租房</option>
                      <option value="与家人同住">与家人同住</option>
                      <option value="暂无住房">暂无住房</option>
                    </select>

                    <select
                      value={religion}
                      onChange={(e) => setReligion(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">宗教信仰</option>
                      <option value="无">无</option>
                      <option value="佛教">佛教</option>
                      <option value="基督教">基督教</option>
                      <option value="天主教">天主教</option>
                      <option value="东正教">东正教</option>
                      <option value="伊斯兰教">伊斯兰教</option>
                      <option value="道教">道教</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-2">用户类型</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={verifiedOnly}
                        onChange={(e) => setVerifiedOnly(e.target.checked)}
                        className="rounded border-pink-300 text-pink-500 focus:ring-pink-300"
                      />
                      仅看认证会员
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={memberOnly}
                        onChange={(e) => setMemberOnly(e.target.checked)}
                        className="rounded border-pink-300 text-pink-500 focus:ring-pink-300"
                      />
                      仅看付费会员
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={questionnaireOnly}
                        onChange={(e) => setQuestionnaireOnly(e.target.checked)}
                        className="rounded border-pink-300 text-pink-500 focus:ring-pink-300"
                      />
                      仅看已完成测评
                    </label>
                  </div>
                </div>
              </div>
            )}
          </form>

          {loading ? (
            <p className="text-center text-gray-400 py-12">加载中...</p>
          ) : users.length === 0 ? (
            <p className="text-center text-gray-400 py-12">暂无符合条件的用户</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="bg-white p-4 rounded-xl shadow hover:scale-[1.02] transition"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/user/${u.id}`)}
                    className="w-full text-left"
                  >
                    <div className="relative w-20 mx-auto mb-3">
                      <img
                        src={
                          u.avatar
                            ? assetUrl(u.avatar)
                            : "https://via.placeholder.com/120"
                        }
                        alt={u.name}
                        className="w-20 h-20 rounded-full object-cover mx-auto"
                      />
                    </div>

                    <p className="font-bold text-lg text-center mt-2 hover:text-pink-600">
                      {u.name}
                    </p>
                    <div className="flex justify-center mt-2" onClick={(e) => e.stopPropagation()}>
                      <VerificationBadges
                        verification={u.verification}
                        userId={u.id}
                        userName={u.name}
                        variant="compact"
                      />
                    </div>
                    <p className="text-sm text-gray-500 text-center mt-2">
                      {u.age ? `${u.age}岁` : "年龄未填"} ·{" "}
                      {formatCityLabel(u.province, u.city, u.district)}
                    </p>
                    {formatLastActive(u.last_active_at) ? (
                      <p className="text-xs text-green-500 text-center mt-1">
                        {formatLastActive(u.last_active_at)}
                      </p>
                    ) : null}
                  </button>

                  <div className="text-sm text-gray-600 mt-3 space-y-1">
                    <p>
                      婚姻：
                      {u.visibility?.locked_fields?.includes("marital_status")
                        ? "🔒 完善后可见"
                        : u.marital_status || "未填写"}
                      {u.gender ? ` · ${u.gender}` : ""}
                    </p>
                    <p>
                      学历：
                      {u.visibility?.locked_fields?.includes("education")
                        ? "🔒 完善后可见"
                        : u.education || "未填写"}
                    </p>
                    <p>
                      职业：
                      {u.visibility?.locked_fields?.includes("job")
                        ? "🔒 完善后可见"
                        : u.job || "未填写"}
                    </p>
                    <p>
                      身高：
                      {u.visibility?.locked_fields?.includes("height")
                        ? "🔒 完善后可见"
                        : u.height
                        ? `${u.height}cm`
                        : "未填写"}
                    </p>
                    <p>
                      月收入：
                      {u.visibility?.locked_fields?.includes("monthly_income")
                        ? "🔒 完善后可见"
                        : u.monthly_income || "未填写"}
                    </p>
                  </div>

                  <p className="text-sm text-gray-500 mt-3 line-clamp-2">
                    {u.visibility?.locked_fields?.includes("bio")
                      ? "🔒 完善自我介绍后可查看对方介绍"
                      : u.bio || "这个人很懒，还没写自我介绍"}
                  </p>

                  <div className="mt-4">
                    <button
                      onClick={() => navigate(`/user/${u.id}`)}
                      className="w-full bg-rose-600 text-white py-2 rounded-full text-sm font-medium"
                    >
                      查看资料
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
